import React, { useEffect, useState } from 'react';
import {
    X,
    Check,
    MoreVertical,
    Pencil,
    Trash2,
    Eye,
    Upload,
    Map as MapIcon,
    Layers,
} from 'lucide-react';
import useStore from '@/store';
import { apiService as api } from '@/services/api/mobileApiService';
import BottomDrawer from '@/components/ui/BottomDrawer';
import OpenStreetMapModal from '@/components/ui/OpenStreetMapModal';
import UploadFloorPlanFields from '@/components/forms/UploadFloorPlanFields';
import { logger } from '@/utils/logger';
import type { FloorPlan, Project } from '@/types/index';
import type { MapCaptureResult } from '@/services/mapCaptureService';

/**
 * Floor-plan management drawer. Replaces the old full-screen
 * FloorPlanManagementScreen with the same per-row + kebab pattern
 * shipped on the project picker.
 *
 * Per-row UX:
 *   - Tap row body  → set floor plan active + close drawer + camera
 *   - Tap ⋮ kebab    → action strip: Rename | Delete
 *   - Rename morphs the row into an inline edit input (Enter saves)
 *   - Delete confirms via window.confirm
 *
 * Footer (list view):
 *   - Upload image → morphs the drawer into the inline upload form
 *                    (UploadFloorPlanFields). Cancel/Submit return
 *                    to the list view.
 *   - Use map      → opens OpenStreetMapModal in-place
 *
 * Map-capture flow:
 *   - OSM modal returns a MapCaptureResult
 *   - A nested name drawer collects the floor plan name
 *   - Submit POSTs the captured map images + name, then refreshes
 *
 * Open/close is driven by the global `floorPlanDrawerProjectId` flag
 * in uiSlice (commit fb74718), so any caller can pop this drawer
 * without prop drilling.
 */

/**
 * The slice of the Zustand store this drawer reads/writes. The store's
 * own set/get are intentionally `any` (circular-reference avoidance —
 * see src/types/store.ts), but each selector here can read from this
 * typed shape rather than `any`.
 */
interface FloorPlanDrawerStore {
    floorPlanDrawerProjectId: number | null;
    closeFloorPlanDrawer: () => void;
    projects: Project[] | null;
    floorPlans: FloorPlan[] | null;
    floorPlanImage: FloorPlan | null;
    setCurrentProject: (p: Project | null) => void;
    setFloorPlanImage: (fp: FloorPlan | null) => void;
    setCurrentScreen: (name: string) => void;
    updateFloorPlan: (fp: FloorPlan) => void;
    deleteFloorPlan: (id: number) => void;
    fetchFloorPlans: (id: number) => Promise<void>;
}

/** Narrow an unknown thrown value to a readable message. */
const errorMessage = (err: unknown): string | undefined =>
    err instanceof Error ? err.message : typeof err === 'string' ? err : undefined;

/**
 * Unwrap whatever the rename endpoint hands back. The server has been
 * observed returning the floor plan directly, wrapped once (`{ data }`)
 * or wrapped twice (`{ data: { data } }`), so probe each shape and
 * return the first that looks like a persisted FloorPlan (has an id).
 */
const unwrapFloorPlan = (response: unknown): FloorPlan | null => {
    const candidates: unknown[] = [
        (response as { data?: { data?: unknown } })?.data?.data,
        (response as { data?: unknown })?.data,
        response,
    ];
    for (const candidate of candidates) {
        if (
            typeof candidate === 'object' &&
            candidate !== null &&
            typeof (candidate as { id?: unknown }).id !== 'undefined' &&
            (candidate as { id?: unknown }).id !== null
        ) {
            return candidate as FloorPlan;
        }
    }
    return null;
};

const cx = (...parts: Array<string | false | undefined>): string =>
    parts.filter(Boolean).join(' ');

const FloorPlanBottomSheet: React.FC = () => {
    const projectId = useStore((s: FloorPlanDrawerStore) => s.floorPlanDrawerProjectId);
    const closeDrawer = useStore((s: FloorPlanDrawerStore) => s.closeFloorPlanDrawer);
    const projects = useStore((s: FloorPlanDrawerStore) => s.projects);
    const floorPlans = useStore((s: FloorPlanDrawerStore) => s.floorPlans);
    const floorPlanImage = useStore((s: FloorPlanDrawerStore) => s.floorPlanImage);
    const setCurrentProject = useStore((s: FloorPlanDrawerStore) => s.setCurrentProject);
    const setFloorPlanImage = useStore((s: FloorPlanDrawerStore) => s.setFloorPlanImage);
    const setCurrentScreen = useStore((s: FloorPlanDrawerStore) => s.setCurrentScreen);
    const updateFloorPlanInStore = useStore((s: FloorPlanDrawerStore) => s.updateFloorPlan);
    const deleteFloorPlanInStore = useStore((s: FloorPlanDrawerStore) => s.deleteFloorPlan);
    const fetchFloorPlans = useStore((s: FloorPlanDrawerStore) => s.fetchFloorPlans);

    const open = projectId !== null;
    const project = projects?.find((p) => p.id === projectId) ?? null;
    const projectFloorPlans = floorPlans?.filter((fp) => fp.project_id === projectId) ?? [];

    // Drawer view — list of plans vs inline upload form. Map naming
    // is a separate nested drawer so doesn't go in this state.
    const [view, setView] = useState<'list' | 'upload'>('list');

    // Per-row interaction state
    const [actionsOpenFor, setActionsOpenFor] = useState<number | null>(null);
    const [renamingId, setRenamingId] = useState<number | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [busyId, setBusyId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Map-capture sub-flow state
    const [osmOpen, setOsmOpen] = useState(false);
    const [pendingMapCapture, setPendingMapCapture] = useState<MapCaptureResult | null>(null);
    const [mapName, setMapName] = useState('');
    const [isUploadingMap, setIsUploadingMap] = useState(false);

    // Reset all transient state whenever the drawer closes.
    useEffect(() => {
        if (!open) {
            setView('list');
            setActionsOpenFor(null);
            setRenamingId(null);
            setRenameValue('');
            setBusyId(null);
            setError(null);
            setOsmOpen(false);
            setPendingMapCapture(null);
            setMapName('');
            setIsUploadingMap(false);
        }
    }, [open]);

    const close = (): void => {
        closeDrawer();
    };

    const useFloorPlan = (fp: FloorPlan): void => {
        if (project) setCurrentProject(project);
        setFloorPlanImage(fp);
        setCurrentScreen('camera');
        close();
    };

    const toggleActions = (id: number): void => {
        setActionsOpenFor((prev) => (prev === id ? null : id));
        setRenamingId(null);
        setError(null);
    };

    const startRename = (fp: FloorPlan): void => {
        setRenamingId(fp.id);
        setRenameValue(fp.name);
        setActionsOpenFor(null);
        setError(null);
    };

    const cancelRename = (): void => {
        setRenamingId(null);
        setRenameValue('');
    };

    const saveRename = async (fp: FloorPlan): Promise<void> => {
        const next = renameValue.trim();
        if (!next) {
            setError('Name cannot be empty');
            return;
        }
        if (next === fp.name) {
            cancelRename();
            return;
        }
        setBusyId(fp.id);
        try {
            const formData = new FormData();
            formData.append('name', next);
            formData.append('project_id', String(fp.project_id));
            const response = await api.put<unknown>(`/api/floorplans/${fp.id}`, formData);
            const updated = unwrapFloorPlan(response);
            if (updated) {
                updateFloorPlanInStore(updated);
            }
            cancelRename();
        } catch (err: unknown) {
            logger.error('Failed to rename floor plan', { error: errorMessage(err) });
            setError('Failed to rename floor plan.');
        } finally {
            setBusyId(null);
        }
    };

    const confirmDelete = async (fp: FloorPlan): Promise<void> => {
        if (!window.confirm(`Delete floor plan "${fp.name}"?`)) return;
        setBusyId(fp.id);
        try {
            await api.delete(`/api/floorplans/${fp.id}`);
            deleteFloorPlanInStore(fp.id);
            if (floorPlanImage?.id === fp.id) {
                setFloorPlanImage(null);
            }
            setActionsOpenFor(null);
        } catch (err: unknown) {
            logger.error('Failed to delete floor plan', { error: errorMessage(err) });
            setError('Failed to delete floor plan.');
        } finally {
            setBusyId(null);
        }
    };

    const onUploadImage = (): void => {
        // Switch to inline upload view — the drawer stays open and
        // its body morphs. Cancel / success return to 'list'.
        setError(null);
        setView('upload');
    };

    const onUploadSuccess = async (): Promise<void> => {
        if (project) {
            try {
                await fetchFloorPlans(project.id);
            } catch (err: unknown) {
                logger.warn('Failed to refresh floor plans after upload', { error: errorMessage(err) });
            }
        }
        setView('list');
    };

    const onUseMap = (): void => {
        setOsmOpen(true);
    };

    const onMapConfirm = (result: MapCaptureResult): void => {
        setPendingMapCapture(result);
        setOsmOpen(false);
        setMapName('');
    };

    const cancelMapNaming = (): void => {
        setPendingMapCapture(null);
        setMapName('');
    };

    const submitMap = async (): Promise<void> => {
        if (!pendingMapCapture || !project) return;
        const name = mapName.trim();
        if (!name) {
            setError('Please provide a name for the map');
            return;
        }
        setIsUploadingMap(true);
        try {
            const formData = new FormData();
            const mapFile = new File([pendingMapCapture.detailBlob], 'map-floor-plan.jpg', {
                type: 'image/jpeg',
            });
            formData.append('image', mapFile);
            const overviewFile = new File(
                [pendingMapCapture.overviewBlob],
                'map-overview.jpg',
                { type: 'image/jpeg' },
            );
            formData.append('overview_image', overviewFile);
            formData.append('name', name);
            formData.append('project_id', String(project.id));
            formData.append('is_map_based', 'true');
            formData.append('map_center', JSON.stringify(pendingMapCapture.center));
            formData.append('detail_zoom', String(pendingMapCapture.detailZoom));
            formData.append('overview_zoom', String(pendingMapCapture.overviewZoom));

            await api.post('/api/floorplans', formData);
            await fetchFloorPlans(project.id);
            cancelMapNaming();
        } catch (err: unknown) {
            logger.error('Failed to upload map floor plan', { error: errorMessage(err) });
            setError(`Map upload failed: ${errorMessage(err) || 'unknown error'}`);
        } finally {
            setIsUploadingMap(false);
        }
    };

    // Footer is shown in list view only. The upload view ships its
    // own Cancel / Upload row inside UploadFloorPlanFields, so the
    // drawer's footer slot stays empty in that mode.
    const listFooter = (
        <div className="flex justify-center gap-2">
            <button
                type="button"
                onClick={onUploadImage}
                className="flex items-center justify-center gap-2 px-8 bg-black text-yellow-300 font-bold rounded-xl border-2 border-black hover:bg-black/90 active:scale-[0.98] transition-all"
                style={{
                    minHeight: 'var(--touch-target)',
                    fontSize: 'clamp(13px, 3.4vw, 15px)',
                }}
            >
                <Upload size={16} aria-hidden="true" />
                <span>Upload image</span>
            </button>
            {/* "Use map" temporarily hidden — the OSM map-capture flow is
                disabled for now. display:none so it takes no layout space and
                the remaining "Upload image" button stays centered. Kept mounted
                so re-enabling later is a one-line change. */}
            <button
                type="button"
                onClick={onUseMap}
                aria-hidden="true"
                tabIndex={-1}
                className="flex items-center justify-center gap-2 bg-black text-yellow-300 font-bold rounded-xl border-2 border-black hover:bg-black/90 active:scale-[0.98] transition-all"
                style={{
                    minHeight: 'var(--touch-target)',
                    fontSize: 'clamp(13px, 3.4vw, 15px)',
                    display: 'none',
                }}
            >
                <MapIcon size={16} aria-hidden="true" />
                <span>Use map</span>
            </button>
        </div>
    );

    // ─── Render ───────────────────────────────────────────────────

    return (
        <>
            <BottomDrawer
                open={open}
                onClose={view === 'upload' ? () => setView('list') : close}
                title={view === 'upload' ? 'Upload floor plan' : 'Floor plans'}
                footer={view === 'list' ? listFooter : undefined}
            >
                {/* Project name pill — context cue under the title. */}
                {project && (
                    <div className="mb-3 flex items-center gap-2">
                        <span
                            className="inline-flex items-center gap-1.5 rounded-full bg-black/10 border border-black/25 text-black px-2.5 py-1"
                            style={{ fontSize: 'clamp(11px, 2.8vw, 13px)' }}
                        >
                            <Layers size={13} aria-hidden="true" />
                            <span className="font-semibold truncate max-w-[60vw]">{project.name}</span>
                            <span className="text-black/60">
                                · {projectFloorPlans.length} plan{projectFloorPlans.length === 1 ? '' : 's'}
                            </span>
                        </span>
                    </div>
                )}

                {error && (
                    <div
                        role="alert"
                        className="mb-2 rounded-xl border border-red-500/60 bg-red-500/15 px-3 py-2 text-red-700"
                        style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}
                    >
                        {error}
                    </div>
                )}

                {/* Body — either the inline upload form, or the
                    floor-plan list with per-row actions. */}
                {view === 'upload' && project ? (
                    <UploadFloorPlanFields
                        project={project}
                        onSuccess={onUploadSuccess}
                        onCancel={() => setView('list')}
                    />
                ) : projectFloorPlans.length === 0 ? (
                    <div
                        className="py-8 text-center text-black/60"
                        style={{ fontSize: 'clamp(13px, 3.6vw, 15px)' }}
                    >
                        No floor plans yet. Use the buttons below to add one.
                    </div>
                ) : (
                    <div className="divide-y divide-black/10">
                        {projectFloorPlans.map((fp) => {
                            const active = floorPlanImage?.id === fp.id;
                            const isRenaming = renamingId === fp.id;
                            const isActionsOpen = actionsOpenFor === fp.id;
                            const isBusy = busyId === fp.id;

                            return (
                                <div key={fp.id} className="py-2">
                                    <div className="w-full flex items-center gap-2">
                                        {isRenaming ? (
                                            <>
                                                <div className="w-10 h-10 rounded-xl bg-black/10 border border-black/20 flex items-center justify-center flex-shrink-0">
                                                    <Pencil size={16} className="text-black/70" />
                                                </div>
                                                <input
                                                    type="text"
                                                    value={renameValue}
                                                    onChange={(e) => setRenameValue(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') saveRename(fp);
                                                        if (e.key === 'Escape') cancelRename();
                                                    }}
                                                    disabled={isBusy}
                                                    autoFocus
                                                    className="flex-1 min-w-0 bg-yellow-100/90 border border-black/40 text-black focus:bg-white focus:outline-none rounded-xl px-3 py-2 font-bold"
                                                    style={{
                                                        fontSize: 'clamp(13px, 3.6vw, 15px)',
                                                        minHeight: 'var(--touch-target)',
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => saveRename(fp)}
                                                    disabled={isBusy || !renameValue.trim()}
                                                    className="flex items-center justify-center rounded-xl bg-black text-yellow-300 hover:bg-black/90 active:scale-95 disabled:opacity-40 transition-all flex-shrink-0"
                                                    style={{ width: 'var(--touch-target)', height: 'var(--touch-target)' }}
                                                    aria-label="Save name"
                                                >
                                                    <Check size={18} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelRename}
                                                    disabled={isBusy}
                                                    className="flex items-center justify-center rounded-xl bg-black/10 text-black hover:bg-black/20 active:scale-95 disabled:opacity-40 transition-all flex-shrink-0"
                                                    style={{ width: 'var(--touch-target)', height: 'var(--touch-target)' }}
                                                    aria-label="Cancel"
                                                >
                                                    <X size={18} />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => useFloorPlan(fp)}
                                                    disabled={isBusy}
                                                    className={cx(
                                                        'flex-1 min-w-0 flex items-center gap-3 py-2 px-1 -mx-1 rounded-xl text-left transition-colors',
                                                        active
                                                            ? 'bg-black/10'
                                                            : 'hover:bg-black/5 active:bg-black/10',
                                                        isBusy && 'opacity-50',
                                                    )}
                                                    style={{ minHeight: 'var(--touch-target)' }}
                                                    aria-current={active ? 'true' : undefined}
                                                >
                                                    {/* Thumbnail */}
                                                    <div
                                                        className={cx(
                                                            'w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 border',
                                                            active
                                                                ? 'bg-black border-black'
                                                                : 'bg-black/10 border-black/20',
                                                        )}
                                                    >
                                                        {fp.image_path ? (
                                                            <img
                                                                src={fp.image_path}
                                                                alt=""
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <Layers
                                                                size={16}
                                                                className={active ? 'text-yellow-300' : 'text-black/70'}
                                                                aria-hidden="true"
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="text-black font-bold truncate leading-tight"
                                                                style={{ fontSize: 'clamp(14px, 3.8vw, 16px)' }}
                                                            >
                                                                {fp.name}
                                                            </span>
                                                            {active && (
                                                                <span
                                                                    className="font-mono font-bold text-yellow-300 bg-black px-1.5 py-0.5 rounded tracking-[0.18em] flex-shrink-0"
                                                                    style={{ fontSize: '9px', lineHeight: 1 }}
                                                                >
                                                                    ACTIVE
                                                                </span>
                                                            )}
                                                        </div>
                                                        {fp.created_at && (
                                                            <div
                                                                className="text-black/55 leading-tight mt-0.5"
                                                                style={{ fontSize: 'clamp(11px, 3vw, 12px)' }}
                                                            >
                                                                {new Date(fp.created_at).toLocaleDateString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleActions(fp.id)}
                                                    disabled={isBusy}
                                                    aria-label="Floor plan actions"
                                                    aria-expanded={isActionsOpen}
                                                    className={cx(
                                                        'flex items-center justify-center rounded-xl transition-colors flex-shrink-0',
                                                        isActionsOpen
                                                            ? 'bg-black/15 text-black'
                                                            : 'text-black/60 hover:bg-black/10 hover:text-black active:bg-black/15',
                                                    )}
                                                    style={{ width: 'var(--touch-target)', height: 'var(--touch-target)' }}
                                                >
                                                    <MoreVertical size={18} />
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    {/* Inline action strip */}
                                    {isActionsOpen && !isRenaming && (
                                        <div className="mt-1 flex gap-2 pl-12 pr-1" role="menu">
                                            <ActionPill
                                                icon={<Eye size={14} />}
                                                label="Use"
                                                onClick={() => useFloorPlan(fp)}
                                                disabled={isBusy}
                                            />
                                            <ActionPill
                                                icon={<Pencil size={14} />}
                                                label="Rename"
                                                onClick={() => startRename(fp)}
                                                disabled={isBusy}
                                            />
                                            <ActionPill
                                                icon={<Trash2 size={14} />}
                                                label="Delete"
                                                onClick={() => confirmDelete(fp)}
                                                disabled={isBusy}
                                                tone="danger"
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </BottomDrawer>

            {/* OpenStreetMap modal — full-screen workflow, opens above
                the drawer. Mounted here so the drawer flow stays self-
                contained; closes back into the name sub-drawer. */}
            <OpenStreetMapModal
                isOpen={osmOpen}
                onClose={() => setOsmOpen(false)}
                onConfirm={onMapConfirm}
            />

            {/* Name-the-map sub-drawer — opens after the OSM modal
                returns a capture. */}
            <BottomDrawer
                open={pendingMapCapture !== null}
                onClose={isUploadingMap ? () => { /* lock during submit */ } : cancelMapNaming}
                title="Name your map floor plan"
                tone="dark"
                footer={
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={cancelMapNaming}
                            disabled={isUploadingMap}
                            className="px-4 rounded-xl border-2 border-yellow-400/40 bg-yellow-400/5 text-yellow-300 hover:bg-yellow-400/15 font-bold transition-colors disabled:opacity-50"
                            style={{
                                minHeight: 'var(--touch-target)',
                                fontSize: 'clamp(13px, 3.4vw, 15px)',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={submitMap}
                            disabled={!mapName.trim() || isUploadingMap}
                            className="flex-1 flex items-center justify-center gap-2 rounded-xl border-2 border-yellow-400 bg-yellow-400 text-black hover:bg-yellow-300 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed font-bold transition-all"
                            style={{
                                minHeight: 'var(--touch-target)',
                                fontSize: 'clamp(13px, 3.6vw, 15px)',
                            }}
                        >
                            {isUploadingMap ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                                    Uploading…
                                </>
                            ) : (
                                'Create floor plan'
                            )}
                        </button>
                    </div>
                }
            >
                <div className="space-y-3 pb-2">
                    <label className="block">
                        <span
                            className="block text-yellow-300/90 font-semibold mb-1"
                            style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}
                        >
                            Floor plan name
                        </span>
                        <input
                            type="text"
                            value={mapName}
                            onChange={(e) => setMapName(e.target.value)}
                            placeholder="e.g. Site Location, Building Exterior"
                            className="w-full px-3 bg-black/50 border border-yellow-400/60 rounded-xl text-yellow-100 placeholder-yellow-400/40 focus:border-yellow-400 focus:outline-none"
                            style={{
                                minHeight: 'var(--touch-target)',
                                fontSize: 'clamp(14px, 3.8vw, 16px)',
                            }}
                            disabled={isUploadingMap}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && mapName.trim()) {
                                    e.preventDefault();
                                    submitMap();
                                }
                            }}
                            autoFocus
                        />
                    </label>
                    <p
                        className="text-yellow-300/60"
                        style={{ fontSize: 'clamp(11px, 2.8vw, 12px)' }}
                    >
                        The map area you captured will be saved as a floor plan with this name.
                    </p>
                </div>
            </BottomDrawer>
        </>
    );
};

interface ActionPillProps {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    tone?: 'neutral' | 'danger';
}

const ActionPill: React.FC<ActionPillProps> = ({ icon, label, onClick, disabled, tone = 'neutral' }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        role="menuitem"
        className={cx(
            'flex-1 flex items-center justify-center gap-1.5 rounded-xl border font-bold transition-all',
            'active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed',
            tone === 'danger'
                ? 'bg-red-500/10 border-red-500/40 text-red-700 hover:bg-red-500/20 active:bg-red-500/30'
                : 'bg-black/5 border-black/25 text-black hover:bg-black/10 active:bg-black/15',
        )}
        style={{
            minHeight: 'min(40px, 11vw)',
            fontSize: 'clamp(11px, 3vw, 13px)',
        }}
    >
        {icon}
        <span>{label}</span>
    </button>
);

export default FloorPlanBottomSheet;
